import get from 'lodash/get'
import pick from 'lodash/pick'
import chain from 'lodash/chain'
import isNil from 'lodash/isNil'
import isUndefined from 'lodash/isUndefined'
import apiClient from '@services/ApiClient'

/**
 * @class Model
 */
export default class Model {
  /**
   * Create model instance.
   *
   * @param {Object} data Initial model data
   */
  constructor (data = {}) {
    this.$model = {}
    this.$loading = false

    const schema = this.getSchema()

    for (const attribute in schema) {
      this
        .addAttribute(attribute, schema[attribute])
        .set(attribute, data[attribute])
    }
  }

  /**
   * Add model attribute.
   *
   * @param {String} name Attribute name
   * @param {Object} schema Attribute schema
   * Example:
   * {
   *   type: Number,
   *   default: 0
   * }
   * @return {Model}
   */
  addAttribute (name, schema) {
    this.$model[name] = get(schema, 'default')
    Object.defineProperty(this, name, this.getAccessors(name))

    return this
  }

  /**
   * Get model attribute accessors.
   *
   * @param  {String} name Attribute name
   * @return {Object}
   */
  getAccessors (name) {
    return {
      get: _ => {
        return this.$model[name]
      },
      set: value => {
        this.$model[name] = value
      }
    }
  }

  /**
   * Get model schema.
   *
   * @return {Object}
   */
  getSchema () {
    return {}
  }

  /**
   * Get an array of field names that will
   * be sent to BE server during save.
   *
   * @return {Array}
   */
  getSavableFields () {
    return chain(this.getSchema())
      .pickBy(field => !field.ignore)
      .keys()
      .value()
  }

  /**
   * Get primary field name.
   * This field is used to determine "new" model state.
   * @see Model#isNew()
   *
   * @return {String|undefined} Field name
   */
  getPrimaryFieldName () {
    return chain(this.getSchema())
      .pickBy(field => field.primary)
      .keys()
      .first()
      .value()
  }

  /**
   * Check whether current model is new.
   *
   * @return {Boolean}
   */
  isNew () {
    return isNil(this.$model[this.getPrimaryFieldName()])
  }

  /**
   * Get raw model data.
   * @see Model#getModelData
   *
   * @return {Object}
   */
  getData () {
    return this.getModelData(this)
  }

  /**
   * Get raw data for specific model.
   *
   * @return {Object}
   */
  getModelData (model) {
    const result = {}

    for (const attribute in model.$model) {
      if (model[attribute] instanceof Model) {
        result[attribute] = this.getModelData(model[attribute])
      } else {
        result[attribute] = model[attribute]
      }
    }

    return result
  }

  /**
   * Get attribute value.
   * @see https://lodash.com/docs/4.17.4#get
   *
   * @param {String} path Attribute path
   * @return {} Attribute value
   */
  get (path) {
    return get(this, path)
  }

  /**
   * Set attribute value.
   * Please note that value type will be casted to the one defined in model schema.
   *
   * @param {String} path Attribute name
   * @param {} value Attribute value
   * @return {Model}
   */
  set (name, value) {
    const attrSchema = this.getSchema()[name]

    const attrType = get(attrSchema, 'type')
    let attrValue = value

    if (isUndefined(attrValue) && !isUndefined(attrSchema.default)) {
      attrValue = attrSchema.default
    }

    if (isUndefined(attrType) || isNil(attrValue) || attrType.name === 'Array') {
      this.$model[name] = attrValue
    } else {
      /* eslint-disable new-cap */
      this.$model[name] = new attrType(attrValue).valueOf()
    }

    return this
  }

  /**
   * Populate model with data.
   * Please note that value type will be casted to the one defined in model schema.
   * @see Model#set()
   *
   * Example: this.setData({
   *   'guid': 'sadhas44j4dhj44s243hd',
   *   'user': {
   *     'name': 'John',
   *     'age': 18
   *   }
   * })
   *
   * @param {Object} data
   * @return {Model}
   */
  setData (data) {
    const schema = this.getSchema()

    for (const attribute in schema) {
      this.set(attribute, data[attribute])
    }

    return this
  }

  /**
   * Set loading state.
   *
   * @param {Boolean} loading
   * @return {Model}
   */
  setLoading (loading) {
    this.$loading = loading
    return this
  }

  /**
   * Get loading state.
   *
   * @return {Boolean}
   */
  isLoading () {
    return this.$loading
  }

  /**
   * Send save request to server.
   * @todo Use POST/PUT method by default
   *
   * @param  {String} resource Resource path
   * @return {Promise}
   */
  save (resource) {
    return this.request(
      this.isNew() ? 'POST' : 'PUT',
      resource,
      pick(this.getData(), this.getSavableFields())
    )
  }

  /**
   * Load model data from BE server.
   *
   * @param  {String} resource Resource path
   * @param  {Object} data Request data
   * @return {Promise}
   */
  fetch (resource) {
    return this.request('GET', resource)
      .then(model => {
        return this.setData(model)
      })
  }

  /**
   * Send data to server.
   *
   * @param  {String} method Request method
   * @param  {String} resource Resource path
   * @param  {Object} data Request data
   * @return {Promise}
   */
  request (method, resource, data) {
    this.setLoading(true)

    return apiClient.sendRequest(method, resource, data)
      .finally(_ => {
        this.setLoading(false)
      })
      .then(data => {
        return data.payload
      })
  }
}



// WEBPACK FOOTER //
// ./src/models/Model.js